import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE } from '../../utils/api'

interface Property {
    _id: string;
    propertyCategory?: string;
    subCategory?: string;
    propertyType: string;
    price: number;
    priceMin?: number;
    priceMax?: number;
    size?: number;
    buildingName?: string;
    area: string;
    city?: string;
    nearbyAddress?: string;
    bhkConfiguration?: string;
    furnishingType?: string;
    description?: string;
    createdAt: string;
    owner?: { name: string; email: string; role: string };
}

export default function AdminBrokerProperties() {
    const { token } = useAuth()
    const [properties, setProperties] = useState<Property[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!token) { setLoading(false); return }
        fetch(`${API_BASE}/classified-properties`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setProperties(data) })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [token])

    // Filter properties for broker/agent
    const brokerProps = properties.filter(p => {
        const role = (p.owner?.role || '').toLowerCase();
        return role === 'broker' || role === 'agent';
    });

    // Calculate dynamic stats
    let totalProperty = brokerProps.length;
    let resRent = 0;
    let resSell = 0;
    let commRent = 0;
    let commSell = 0;

    const today = new Date();
    today.setHours(0,0,0,0);
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    let addedToday = 0;
    let addedThisWeek = 0;
    let addedThisMonth = 0;

    const last7DaysCounts = [0, 0, 0, 0, 0, 0, 0];
    const daysMap = [6, 0, 1, 2, 3, 4, 5]; // Sun=6, Mon=0, ..., Sat=5

    brokerProps.forEach(p => {
        const type = (p.propertyType || '').toLowerCase();
        const cat = (p.propertyCategory || '').toLowerCase();
        
        if (cat.includes('residential') || type.includes('residential')) {
            if (type.includes('rent')) resRent++;
            else resSell++;
        } else if (cat.includes('commercial') || type.includes('commercial')) {
            if (type.includes('rent')) commRent++;
            else commSell++;
        } else {
            if (type.includes('rent')) resRent++;
            else resSell++;
        }

        if (p.createdAt) {
            const d = new Date(p.createdAt);
            if (d >= today) addedToday++;
            if (d >= startOfWeek) addedThisWeek++;
            if (d >= startOfMonth) addedThisMonth++;

            const daysDiff = Math.floor((today.getTime() - d.getTime()) / (1000 * 3600 * 24));
            if (daysDiff >= 0 && daysDiff < 7) {
                const dayIdx = daysMap[d.getDay()];
                if (dayIdx !== undefined) last7DaysCounts[dayIdx]++;
            }
        }
    });

    const maxChartCount = Math.max(...last7DaysCounts, 1);

    return (
        <AdminLayout>
            {/* Header section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 select-none">
                <div>
                    <h1 className="text-4xl font-extrabold text-[#0f172a] mb-2 leading-none tracking-tight">Broker Properties</h1>
                </div>
                <div className="flex items-center gap-4 self-end md:self-center">
                    <Link to="/admin-add-property?for=broker" className="px-6 py-3 bg-[#5B32B4] hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 shadow-md">
                        <i className="fa-solid fa-plus text-xs"></i> Add Property
                    </Link>
                    <Link to="/admin-saved-properties" className="px-5 py-3 bg-indigo-50 hover:bg-indigo-100 text-[#5B32B4] rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 border border-indigo-100 shadow-sm cursor-pointer">
                        <i className="fa-solid fa-magnifying-glass text-xs"></i> Search Property
                    </Link>
                </div>
            </div>

            {/* 5 Box Metrics Row exactly matching mockup */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-10 select-none">
                {/* Total Property */}
                <Link to="/admin-saved-properties" className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-center gap-4 hover:shadow-md hover:border-indigo-100 transition-all">
                    <div className="w-14 h-14 rounded-full bg-indigo-50 text-[#5B32B4] flex items-center justify-center text-xl flex-shrink-0 shadow-sm">
                        <i className="fa-solid fa-house-chimney"></i>
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-slate-800 leading-none">#{totalProperty.toLocaleString()}</h3>
                        <p className="text-xs font-medium text-slate-400 mt-1">Total Property</p>
                    </div>
                </Link>

                {/* Residential Rent */}
                <Link to="/admin-saved-properties" className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-center gap-4 hover:shadow-md hover:border-indigo-100 transition-all">
                    <div className="w-14 h-14 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center text-xl flex-shrink-0 shadow-sm">
                        <i className="fa-solid fa-key"></i>
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-slate-800 leading-none">#{resRent.toLocaleString()}</h3>
                        <p className="text-xs font-medium text-slate-400 mt-1">Residential Rent</p>
                    </div>
                </Link>

                {/* Residential Sell */}
                <Link to="/admin-saved-properties" className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-center gap-4 hover:shadow-md hover:border-indigo-100 transition-all">
                    <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl flex-shrink-0 shadow-sm">
                        <i className="fa-solid fa-building"></i>
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-slate-800 leading-none">#{resSell.toLocaleString()}</h3>
                        <p className="text-xs font-medium text-slate-400 mt-1">Residential Sell</p>
                    </div>
                </Link>

                {/* Commercial Rent */}
                <Link to="/admin-saved-properties" className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-center gap-4 hover:shadow-md hover:border-indigo-100 transition-all">
                    <div className="w-14 h-14 rounded-full bg-pink-50 text-pink-600 flex items-center justify-center text-xl flex-shrink-0 shadow-sm">
                        <i className="fa-solid fa-shop"></i>
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-slate-800 leading-none">#{commRent.toLocaleString()}</h3>
                        <p className="text-xs font-medium text-slate-400 mt-1">Commercial Rent</p>
                    </div>
                </Link>

                {/* Commercial Sell */}
                <Link to="/admin-saved-properties" className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-center gap-4 hover:shadow-md hover:border-indigo-100 transition-all">
                    <div className="w-14 h-14 rounded-full bg-sky-50 text-sky-600 flex items-center justify-center text-xl flex-shrink-0 shadow-sm">
                        <i className="fa-solid fa-briefcase"></i>
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-slate-800 leading-none">#{commSell.toLocaleString()}</h3>
                        <p className="text-xs font-medium text-slate-400 mt-1">Commercial Sell</p>
                    </div>
                </Link>
            </div>

            {/* Activity Summary Divider */}
            <div className="text-center mb-8 select-none">
                <span className="text-lg font-bold text-slate-800 relative inline-block pb-1 border-b-2 border-[#5B32B4] tracking-wide">
                    Activity Summary
                </span>
            </div>

            {/* 3 Activity Metrics Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 select-none">
                {/* Added Today */}
                <Link to="/admin-saved-properties" className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-center gap-5 hover:shadow-md hover:border-indigo-100 transition-all">
                    <div className="w-14 h-14 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-600 flex items-center justify-center text-xl shadow-sm">
                        <i className="fa-regular fa-calendar-check"></i>
                    </div>
                    <div>
                        <h4 className="text-3xl font-extrabold text-slate-800 leading-none">{addedToday}</h4>
                        <p className="text-xs font-bold text-slate-500 mt-1.5 uppercase tracking-wide">Added Today</p>
                    </div>
                </Link>

                {/* This Week */}
                <Link to="/admin-saved-properties" className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-center gap-5 hover:shadow-md hover:border-indigo-100 transition-all">
                    <div className="w-14 h-14 rounded-xl bg-indigo-50 border border-indigo-100 text-[#5B32B4] flex items-center justify-center text-xl shadow-sm">
                        <i className="fa-solid fa-arrow-up-right-dots"></i>
                    </div>
                    <div>
                        <h4 className="text-3xl font-extrabold text-slate-800 leading-none">{addedThisWeek}</h4>
                        <p className="text-xs font-bold text-slate-500 mt-1.5 uppercase tracking-wide">This Week</p>
                    </div>
                </Link>

                {/* This Month */}
                <Link to="/admin-saved-properties" className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-center gap-5 hover:shadow-md hover:border-indigo-100 transition-all">
                    <div className="w-14 h-14 rounded-xl bg-amber-50 border border-amber-100 text-amber-600 flex items-center justify-center text-xl shadow-sm">
                        <i className="fa-regular fa-chart-bar"></i>
                    </div>
                    <div>
                        <h4 className="text-3xl font-extrabold text-slate-800 leading-none">{addedThisMonth}</h4>
                        <p className="text-xs font-bold text-slate-500 mt-1.5 uppercase tracking-wide">This Month</p>
                    </div>
                </Link>
            </div>

            {/* Properties Flow Bar Chart exactly matching mockup */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm select-none">
                <h3 className="text-sm font-bold text-slate-800 mb-8 tracking-wide">Properties Flow — Last 7 Days</h3>
                
                <div className="flex flex-col gap-2">
                    {/* Y Axis grid and chart area */}
                    <div className="relative h-[240px] flex">
                        {/* Y axis labels */}
                        <div className="flex flex-col justify-between text-xs font-bold text-slate-400 w-10 pr-4 select-none">
                            <span>140</span>
                            <span>105</span>
                            <span>70</span>
                            <span>35</span>
                            <span>0</span>
                        </div>

                        {/* Faint Grid Lines & Bars */}
                        <div className="flex-1 border-b border-slate-100 relative">
                            {/* Horizontal grid guide lines */}
                            <div className="absolute top-0 left-0 w-full h-full flex flex-col justify-between pointer-events-none select-none">
                                <div className="border-t border-dashed border-slate-100 w-full h-0"></div>
                                <div className="border-t border-dashed border-slate-100 w-full h-0"></div>
                                <div className="border-t border-dashed border-slate-100 w-full h-0"></div>
                                <div className="border-t border-dashed border-slate-100 w-full h-0"></div>
                            </div>

                            {/* Column Bars */}
                            <div className="absolute inset-0 flex items-end justify-around px-2">
                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                                    const val = last7DaysCounts[idx];
                                    const pct = val === 0 ? 3 : (val / maxChartCount) * 85; // slightly minimal height
                                    return (
                                        <Link to="/admin-saved-properties" key={day} className="flex flex-col items-center gap-1 group relative flex-1 max-w-[50px] mx-1">
                                            {/* Top Tooltip */}
                                            <div className="absolute -top-8 bg-slate-800 text-white font-bold text-[10px] px-2 py-1 rounded shadow-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                                {val} listings
                                            </div>

                                            {/* Bar component exactly matching colors from mockup */}
                                            <div 
                                                style={{ height: `${pct}%` }} 
                                                className="w-[28px] md:w-[34px] bg-[#5B32B4] hover:bg-slate-800 rounded-t-lg transition-all duration-300 shadow-sm cursor-pointer"
                                            ></div>
                                        </Link>
                                    )
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Bottom axis days matching mockup */}
                    <div className="flex ml-10 justify-around px-2 pt-2 border-t border-transparent select-none">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                            <span key={day} className="text-xs font-bold text-slate-500 w-[28px] md:w-[34px] text-center">
                                {day}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </AdminLayout>
    )
}
